import { deleteAccount } from "apis/settingsServices";
import React, { useState } from "react";
import { useToastStore } from "store/toastStore";
import { useStore } from "store/zustand";
 // 👈 make sure this path is correct

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setIsAuthenticated } = useStore(); // 👈 Auth store

  if (!isOpen) return null;

  const handleModalInteraction = (e: React.MouseEvent) => e.stopPropagation();

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setError(null);
    onClose();
  };

  const handleDeleteAccount = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLoading(true);
    setError(null);

    try {
      await deleteAccount();
      useToastStore.getState().showToast("Account deleted successfully", "success", 3000);

      // ✅ Logout after delete
      setIsAuthenticated(false);
      localStorage.removeItem("token");
      localStorage.removeItem("userDetails");

      onClose();
    } catch (err) {
      setError("Failed to delete account. Please try again.");
      useToastStore.getState().showToast("Error deleting account", "error", 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      id="delete-account-modal"
      tabIndex={-1}
      aria-hidden="true"
      className="fixed top-0 right-0 left-0 z-50 flex justify-center items-center w-full h-screen bg-black bg-opacity-50"
      onClick={handleClose}
    >
      <div className="relative p-4 w-full max-w-xl" onClick={handleModalInteraction}>
        <div className="relative bg-white rounded-lg shadow">
          <div className="flex items-center justify-between p-4 md:p-5 rounded-t">
            <h3 className="text-lg font-semibold text-grease-100">Delete Account</h3>
            <button
              type="button"
              className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              onClick={handleClose}
              disabled={isLoading}
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>

          <div className="p-4 border-t">
            <p className="text-sm text-grease-200">
              You are about to permanently delete your account. This action cannot be undone.
              Are you sure you want to proceed?
            </p>
            <div className="bg-red-50 rounded-lg p-5 flex gap-4 mt-5">
              <i className="st-info text-2xl text-red-500"></i>
              <div>
                <p className="text-sm text-grease-200">
                  All your saved data will be lost and this action is irreversible.
                </p>
              </div>
            </div>
            {error && (
              <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {error}
              </div>
            )}
          </div>

          <div className="flex gap-4 items-center justify-end p-4 md:p-5 border-t border-gray-200 rounded-b">
            <button
              className="flex items-center gap-2 p-3 bg-indigo-100 rounded-lg"
              onClick={handleClose}
              disabled={isLoading}
            >
              <p className="font-medium bg-skyblue-200 text-grease-50 text-sm">Cancel</p>
            </button>
            <button
              className={`flex items-center gap-2 p-3 ${isLoading ? 'bg-gray-400' : 'bg-red-500'} rounded-lg`}
              onClick={handleDeleteAccount}
              disabled={isLoading}
            >
              <p className="font-medium text-white text-sm">
                {isLoading ? "Processing..." : "Delete Account"}
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
